<template>
  <v-row>
    <v-col cols="12">
      <v-card class="cn-card" flat>
        <div class="cn-header pl-base-bg">
          <div class="cn-header-left">
            <v-icon color="#fff" size="18" class="mr-3">mdi-file-document-minus-outline</v-icon>
            <div>
              <div class="cn-title">Credit Note #{{ creditNote?.creditNoteNumber }}</div>
              <div class="cn-subtitle">
                Against Invoice #{{ creditNote?.invoiceNumber }} &middot; {{ formatDate(creditNote?.creditNoteDate) }}
              </div>
            </div>
          </div>

          <div class="cn-header-right">
            <v-chip
              :color="statusColor(creditNote?.status)"
              dark
              small
              class="text-uppercase font-weight-bold"
            >
              {{ creditNote?.status }}
            </v-chip>

            <v-btn v-if="!isViewOnly" small class="cn-edit-btn ml-2" :to="`/credit-notes/${creditNote?._id}/edit`">
              Edit
            </v-btn>

            <v-btn
              v-if="creditNote?.status === 'ISSUED' && !creditNote?.transactionId && !isViewOnly && remainingRefundable > 0.01"
              small
              class="cn-refund-btn ml-2"
              @click="openRefundDialog"
            >
              Record Refund
            </v-btn>

            <v-btn
              v-if="creditNote?.status === 'ISSUED' && !isViewOnly"
              small
              class="cn-cancel-btn ml-2"
              :disabled="!!creditNote?.transactionId"
              @click="confirmCancel = true"
            >
              Cancel Note
            </v-btn>
          </div>
        </div>

        <div class="pa-6">
          <v-alert v-if="creditNote?.transactionId" type="success" outlined dense border="left" class="mb-4">
            Refund of {{ formatAmount(creditNote.totalAmount) }} has been recorded against this credit note.
          </v-alert>

          <v-alert
            v-else-if="creditNote?.status === 'ISSUED' && remainingRefundable <= 0.01"
            type="info"
            outlined
            dense
            border="left"
            class="mb-4"
          >
            No refund can be recorded yet — the customer hasn't paid anything on this invoice, so there's no cash to return.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <div class="cn-party-card">
                <div class="cn-party-label">Customer</div>
                <div class="cn-party-name">{{ creditNote?.customer?.name }}</div>
                <div class="cn-party-line" v-if="creditNote?.customer?.address">
                  {{ creditNote?.customer?.address }}
                </div>
                <div class="cn-party-line" v-if="creditNote?.customer?.gstin">
                  GSTIN: {{ creditNote?.customer?.gstin }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="cn-party-card">
                <div class="cn-party-label">Original Invoice</div>
                <div class="cn-party-name">
                  <router-link :to="`/invoices/${creditNote?.invoiceId}/view`">
                    #{{ creditNote?.invoiceNumber }}
                  </router-link>
                </div>
                <div class="cn-party-line">{{ formatDate(creditNote?.invoiceDate) }}</div>
                <div class="cn-party-line">Reason: {{ reasonLabel(creditNote?.reason) }}</div>
              </div>
            </v-col>
          </v-row>

          <div class="cn-items-wrap mt-4">
            <div class="table-scroll">
              <v-simple-table dense class="cn-items-table">
                <thead>
                  <tr>
                    <th class="text-left">Product</th>
                    <th class="text-right">HSN / SAC</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Tax %</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in creditNote?.items" :key="i">
                    <td>{{ item.product }}</td>
                    <td class="text-right">{{ item.hsn }}</td>
                    <td class="text-right">{{ item.quantity }}</td>
                    <td class="text-right">{{ formatAmount(item.price) }}</td>
                    <td class="text-right">{{ item.taxPercent }}%</td>
                    <td class="text-right font-weight-medium">{{ formatAmount(item.amount) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>

          <v-row justify="end" class="mt-4">
            <v-col cols="12" md="4">
              <div class="cn-totals-card">
                <v-row dense>
                  <v-col class="grey--text py-1">Subtotal</v-col>
                  <v-col class="text-right py-1">{{ formatAmount(creditNote?.subtotal) }}</v-col>
                </v-row>

                <template v-if="creditNote?.isInterState">
                  <v-row dense>
                    <v-col class="grey--text py-1">IGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(creditNote?.igst) }}</v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-row dense>
                    <v-col class="grey--text py-1">CGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(creditNote?.cgst) }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="grey--text py-1">SGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(creditNote?.sgst) }}</v-col>
                  </v-row>
                </template>

                <v-divider class="my-2" />

                <v-row dense class="text-h6 font-weight-bold">
                  <v-col>Total</v-col>
                  <v-col class="text-right cn-total-value">{{ formatAmount(creditNote?.totalAmount) }}</v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="creditNote?.note" class="mt-4">
            <v-col>
              <v-alert type="info" outlined dense>{{ creditNote.note }}</v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmCancel"
      v-model="confirmCancel"
      :buttons="cancelButtons"
      title="Cancel Credit Note"
    >
      <p>Are you sure you want to cancel this credit note? The credited amount will be reversed on the original invoice.</p>
    </global-dialog>

    <!-- RECORD REFUND -->
    <global-dialog
      v-model="refundDialog"
      title="Record Refund"
      :buttons="refundDialogButtons"
    >
      <v-row dense>
        <v-col cols="12" class="mt-3">
          <v-alert type="info" dense text>
            Only record this once the refund has actually been paid out to the customer — this creates a real cash-out entry.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-text-field
            :value="formatAmount(creditNote?.totalAmount)"
            label="Refund Amount"
            outlined
            dense
            readonly
          />

          <v-select
            v-model="refundPaymentMethod"
            :items="paymentMethods"
            label="Payment Method"
            outlined
            dense
            required
            class="mt-4"
          />
        </v-col>
      </v-row>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "ViewCreditNote",
  components: { GlobalDialog },

  data() {
    return {
      creditNote: null,
      remainingRefundable: 0,
      confirmCancel: false,
      cancelling: false,
      refundDialog: false,
      refundPaymentMethod: "",
      refunding: false,

      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],
    };
  },

  computed: {
    cancelButtons() {
      return [
        {
          text: "Back",
          action: () => (this.confirmCancel = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Cancel Note",
          action: this.doCancel,
          color: "error",
          disabled: this.cancelling,
          loading: this.cancelling,
        },
      ];
    },

    refundDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.refundDialog = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Record Refund",
          action: this.doRecordRefund,
          color: "primary",
          disabled: !this.refundPaymentMethod || this.refunding,
          loading: this.refunding,
        },
      ];
    },
  },

  async created() {
    await this.fetchCreditNote();
  },

  methods: {
    async fetchCreditNote() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("creditNotes/getCreditNoteById", id);
        this.creditNote = res.creditNote;
        this.remainingRefundable = res.remainingRefundable || 0;
      } catch (err) {
        this.$toast?.error("Failed to load credit note");
      }
    },

    async doCancel() {
      if (this.cancelling) return;
      this.cancelling = true;
      try {
        const res = await this.$store.dispatch("creditNotes/cancelCreditNote", this.creditNote._id);
        this.creditNote = res.creditNote;
        this.$toast?.success("Credit note cancelled");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to cancel credit note");
      } finally {
        this.cancelling = false;
        this.confirmCancel = false;
      }
    },

    openRefundDialog() {
      this.refundPaymentMethod = "";
      this.refundDialog = true;
    },

    async doRecordRefund() {
      if (this.refunding) return;
      this.refunding = true;
      try {
        const res = await this.$store.dispatch("creditNotes/recordRefund", {
          id: this.creditNote._id,
          paymentMethod: this.refundPaymentMethod,
        });
        this.creditNote = res.creditNote;
        this.$toast?.success("Refund recorded");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to record refund");
      } finally {
        this.refunding = false;
        this.refundDialog = false;
      }
    },

    reasonLabel(reason) {
      const map = {
        SALES_RETURN: "Sales Return",
        POST_SALE_DISCOUNT: "Post-Sale Discount",
        BILLING_CORRECTION: "Billing Correction",
        OTHER: "Other",
      };
      return map[reason] || reason || "-";
    },

    statusColor(status) {
      switch (status) {
        case "ISSUED":
          return "green";
        case "CANCELLED":
          return "red";
        default:
          return "grey";
      }
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.cn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.cn-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.cn-header-left {
  display: flex;
  align-items: center;
}

.cn-header-right {
  display: flex;
  align-items: center;
}

.cn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.cn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.cn-edit-btn,
.cn-cancel-btn,
.cn-refund-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.cn-party-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
  height: 100%;
}

.cn-party-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.cn-party-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.cn-party-line {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.cn-items-wrap {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: auto;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.cn-items-table {
  min-width: 560px;
}

.cn-items-table >>> th {
  background: #f8fafc;
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
}

.cn-totals-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
}

.cn-total-value {
  color: #0a5a90;
}
</style>
